import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

interface MobileVirtualListProps<T> {
  items: T[];
  estimateSize: number;
  className?: string;
  empty?: ReactNode;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
}

export function useMobileViewport(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 760px)').matches);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 760px)');
    const update = () => setMobile(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return mobile;
}

/**
 * RecyclerView-style list for the mobile surface. It virtualizes against the
 * window so the page keeps one natural scroll container and variable-height
 * cards are measured after rendering.
 */
export function MobileVirtualList<T>({
  items,
  estimateSize,
  className = '',
  empty,
  getKey,
  renderItem
}: MobileVirtualListProps<T>) {
  const isMobile = useMobileViewport();
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!isMobile || !listRef.current) return;
    const update = () => {
      if (!listRef.current) return;
      setScrollMargin(listRef.current.getBoundingClientRect().top + window.scrollY);
    };
    update();
    const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    observer?.observe(document.body);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isMobile, items.length]);

  const virtualizer = useWindowVirtualizer({
    count: isMobile ? items.length : 0,
    estimateSize: () => estimateSize,
    overscan: 6,
    scrollMargin
  });

  if (!isMobile) return null;
  if (!items.length) return <div className={`mobile-virtual-list ${className}`}>{empty}</div>;

  return (
    <div
      ref={listRef}
      className={`mobile-virtual-list ${className}`}
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((row) => {
        const item = items[row.index]!;
        return (
          <div
            className="mobile-virtual-list__row"
            data-index={row.index}
            key={getKey(item)}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${row.start - scrollMargin}px)` }}
          >
            {renderItem(item)}
          </div>
        );
      })}
    </div>
  );
}
